import React, {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react';
import { RecruitmentShowContentInfoProps, RecruitmentShowContentInfoState } from './types';
import { RecruitmentEntryButton } from '../../atoms/recruitmentEntryButton';

type Props = RecruitmentShowContentInfoProps & {
  state: RecruitmentShowContentInfoState;
};

export const Presentation: FunctionComponent<Props> = ({
  connectedFlag,
  handleRegisterModalOpen,
  recruitmentId,
  isPc,
  isStopped,
  dataCategory,
  dataValue,
  messageBoxId,
  isShowMag,
  state,
  handleClickEntry,
  isPreview,
  handlePreviewClick,
  isStoppedFv,
  hideTopEntryButton = false,
}) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [isFixed, setIsFixed] = useState(false);

  const descriptionRef = useRef<HTMLDivElement>(null);
  const descriptionInnerRef = useRef<HTMLDivElement>(null);
  const descriptionAnchorRef = useRef<HTMLDivElement>(null);

  const [descriptionHeight, setDescriptionHeight] = useState<number>(0);
  const descriptionOriginalTopRef = useRef<number | null>(null);

  const isUserScrollingRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const isClickScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef<number | null>(null);
  const clickScrollTimeoutRef = useRef<number | null>(null);

  const sectionIds = useMemo(
    () => [
      'what-to-do-section',
      'desired-person-section',
      'recruitment-info-section',
      'company-people-section',
      'what-doing-section',
      'company-role-section',
      'company-info-section',
      'company-stories-section',
      'company-mags-section',
      'brand-recruitments-section',
      'company-recruitments-section',
      'occupation-recruitments-section'
    ],
    []
  );

  const [visibleSectionIds, setVisibleSectionIds] = useState<string[]>([]);

  const getSectionDataValue = (sectionId: string): string => {
    switch (sectionId) {
      case 'what-to-do-section': return dataValue.whatToDo;
      case 'desired-person-section': return dataValue.desiredPerson;
      case 'recruitment-info-section': return dataValue.recruitmentInfo;
      case 'company-people-section': return dataValue.companyPeople;
      case 'what-doing-section': return dataValue.whatDoing;
      case 'company-role-section': return dataValue.companyRole;
      case 'company-info-section': return dataValue.companyInfo;
      case 'company-stories-section': return dataValue.companyStories;
      case 'company-mags-section': return dataValue.companyMags;
      case 'brand-recruitments-section': return dataValue.brandRecruitments;
      case 'company-recruitments-section': return dataValue.companyRecruitments;
      case 'occupation-recruitments-section': return dataValue.occupationRecruitments;
      default: return '';
    }
  };

  const getSectionName = (sectionId: string) => {
    switch (sectionId) {
      case 'what-to-do-section': return '何をやるのか';
      case 'desired-person-section': return 'どんな人に来て欲しいか';
      case 'recruitment-info-section': return '募集情報';
      case 'company-people-section': return 'メンバー';
      case 'what-doing-section': return '何をやっているのか';
      case 'company-role-section': return '企業の役割';
      case 'company-info-section': return '会社情報';
      case 'company-stories-section': return 'この企業のストーリー';
      case 'company-mags-section': return 'この企業をもっと知る';
      case 'brand-recruitments-section': return 'このブランドの募集中求人';
      case 'company-recruitments-section': return 'この企業の募集中求人';
      case 'occupation-recruitments-section': return 'この職種の求人';
      default: return '';
    }
  };

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const calc = () => {
      const ids = sectionIds.filter((id) => !!document.getElementById(id));
      setVisibleSectionIds(ids);

      if (ids.length > 0 && activeSection && !ids.includes(activeSection)) {
        setActiveSection(ids[0]);
      }
      if (ids.length > 0 && !activeSection) {
        setActiveSection(ids[0]);
      }
    };

    calc();

    const t0 = window.setTimeout(calc, 0);
    const t1 = window.setTimeout(calc, 200);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [sectionIds, state]);

  // 目次を対象セクションの位置まで横スクロール
  // force=true: ユーザーが目次をスクロール中でも強制実行（クリック時に使用）
  const slideTabToLeft = (sectionId: string, force = false) => {
    if (!force && isUserScrollingRef.current) return;
    // スクロール可能なのは overflow:scroll を持つ外側の .description（descriptionRef）
    const container = descriptionRef.current;
    const inner = descriptionInnerRef.current;
    if (!container || !inner) return;

    const link = inner.querySelector<HTMLAnchorElement>(`a[data-section-id="${sectionId}"]`);
    if (!link) return;

    const PADDING = 16;
    // getBoundingClientRect でビューポート基準の位置を取得し、現在のスクロール量を加算
    const containerRect = container.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const left = Math.max(container.scrollLeft + linkRect.left - containerRect.left - PADDING, 0);

    isProgrammaticScrollRef.current = true;
    container.scrollTo({ left, behavior: 'smooth' });
    window.setTimeout(() => { isProgrammaticScrollRef.current = false; }, 400);
  };

  // 目次クリック: ページを該当セクションへスクロール + 目次を強制的に横スクロール
  const handleSectionClick = (sectionId: string) => {
    // スムーススクロール中に IntersectionObserver が中間セクションを検出して
    // activeSection を上書きするのを防ぐ
    isClickScrollingRef.current = true;
    if (clickScrollTimeoutRef.current) window.clearTimeout(clickScrollTimeoutRef.current);
    clickScrollTimeoutRef.current = window.setTimeout(() => {
      isClickScrollingRef.current = false;
    }, 1000);

    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    slideTabToLeft(sectionId, true);
  };

  // activeSectionが変わったら目次を自動横スクロール（ユーザーが目次を操作中は待機）
  const prevActiveSectionRef = useRef<string>('');
  useEffect(() => {
    if (isStoppedFv && !isFixed) return;
    if (!activeSection) return;
    if (prevActiveSectionRef.current === activeSection) return;
    prevActiveSectionRef.current = activeSection;
    slideTabToLeft(activeSection);
  }, [activeSection, isFixed, isStoppedFv]);

  // ページスクロールでアクティブセクションを検出（IntersectionObserver）
  useEffect(() => {
    if (isPc) return;
    if (visibleSectionIds.length === 0) return;

    const sectionVisibility: Record<string, boolean> = {};

    const updateActiveSection = () => {
      // クリックによるスムーススクロール中は中間セクションの検出を無視する
      if (isClickScrollingRef.current) return;
      const active = visibleSectionIds.find((id) => sectionVisibility[id]);
      if (active) setActiveSection(active);
    };

    const observers = visibleSectionIds.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          sectionVisibility[id] = entry.isIntersecting;
          updateActiveSection();
        },
        // 上から10%〜80%の領域に入ったらアクティブとみなす
        { threshold: 0, rootMargin: '-10% 0px -80% 0px' }
      );
      observer.observe(el);
      return observer;
    });

    return () => observers.forEach((o) => o?.disconnect());
  }, [isPc, visibleSectionIds]);

  useLayoutEffect(() => {
    if (isStoppedFv) return;
    const el = descriptionRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setDescriptionHeight(rect.height);
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, [isStoppedFv]);

  useLayoutEffect(() => {
    const el = isStoppedFv ? descriptionAnchorRef.current : descriptionRef.current;
    if (!el) return;

    const updateOriginalTop = () => {
      const rect = el.getBoundingClientRect();
      descriptionOriginalTopRef.current = rect.top + window.scrollY;
    };

    updateOriginalTop();
    requestAnimationFrame(updateOriginalTop);

    window.addEventListener('resize', updateOriginalTop);
    return () => window.removeEventListener('resize', updateOriginalTop);
  }, [state, isStoppedFv]);

  useEffect(() => {
    if (isPc) return;

    const onScroll = () => {
      const originalTop = descriptionOriginalTopRef.current;
      if (originalTop == null) return;

      if (window.scrollY >= originalTop) {
        if (!isFixed) setIsFixed(true);
      } else {
        if (isFixed) setIsFixed(false);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isPc, isFixed]);

  // 目次の横スクロール中フラグ管理（ユーザーが手動スクロール中は自動スクロールを抑制）
  // isProgrammaticScrollRef が true の場合はプログラム的スクロールなので無視する
  useEffect(() => {
    // スクロールイベントも外側の .description（descriptionRef）で拾う
    const container = descriptionRef.current;
    if (!container) return;

    const markUserScrolling = () => {
      if (isProgrammaticScrollRef.current) return;
      isUserScrollingRef.current = true;
      if (userScrollTimeoutRef.current) window.clearTimeout(userScrollTimeoutRef.current);
      userScrollTimeoutRef.current = window.setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 250);
    };

    container.addEventListener('scroll', markUserScrolling, { passive: true });
    container.addEventListener('wheel', markUserScrolling, { passive: true });

    return () => {
      container.removeEventListener('scroll', markUserScrolling);
      container.removeEventListener('wheel', markUserScrolling);
      if (userScrollTimeoutRef.current) window.clearTimeout(userScrollTimeoutRef.current);
    };
  }, [isFixed, isStoppedFv]);

  const descriptionLinks = (
    <div className="description__inner" ref={descriptionInnerRef}>
      {visibleSectionIds.map((sectionId) => (
        <a
          key={sectionId}
          data-section-id={sectionId}
          href="#"
          className={`description__link ${activeSection === sectionId ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            handleSectionClick(sectionId);
          }}
          data-event='click'
          data-category={isStopped ? dataCategory.stopRecruitmentContent : dataCategory.recruitmentContent}
          data-value={getSectionDataValue(sectionId)}
          data-recruitmentid={recruitmentId}
          data-companyid={state.companyId}
        >
          {getSectionName(sectionId)}
        </a>
      ))}
    </div>
  );

  return (
    <>
      <div className={`recruitment__show__heading__content ${isStoppedFv && isStopped ? 'stopped' : ''}`}>
        {state.companyId !== null && (
          <>
            <h1 className="recruitment__show__heading__title">{state.recruitment_title}</h1>

            {(!isStoppedFv || !isStopped) &&
              <div className="recruitment__show__heading__info">
                {state.employment_types && (
                  <div className="employmentTypes">
                    <a
                      href={`/${state.employment_types[0].enName}`}
                      key={state.employment_types[0].name}
                      className="tag"
                    >
                      <i className="fas fa-user"></i>
                      {state.employment_types[0].name}
                    </a>
                    {/* タグで表示するので一旦コメントアウト */}
                    {/* {state.regularEmployee && <span className="regularEmployee">正社員</span>} */}
                  </div>
                )}

                {state.address !== '' && (
                  <a href={`/${state.prefectureEnName}`} className="address">
                    <i className="fas fa-map-marker-alt"></i>
                    {state.address}
                  </a>
                )}

                {state.salary !== '' && (
                  <a href={state.salarySearchUrl ?? undefined} className="address salary">
                    <i className="fas fa-yen-sign"></i>
                    {state.salary}
                  </a>
                )}
              </div>
            }

            <div className="recruitment__show__heading__icon">
              <span className="icon">
                {state.icon && <img src={state.icon} alt={`${state.company_name}のロゴ写真`} />}
              </span>

              {state.companyId !== null && (
                <a
                  href={`/companies/${state.companyId}`}
                  className="company_name"
                  {...(!isPreview && {
                    'data-event': 'click',
                    'data-category': isStopped
                      ? dataCategory.stopRecruitmentContent
                      : dataCategory.recruitmentContent,
                    'data-value': dataValue.companyName,
                  })}
                >
                  {state.company_name}
                </a>
              )}
            </div>
          </>
        )}

        {/* A/Bテスト（hide_top_entry_button）: ON時は上部ボタンを描画せず、下部追従ボタンを常時表示にする。
            .recruitment-entry-button-first が存在しなければ追従ボタンの非表示処理は走らない。
            募集終了・エントリー済みは対象外として従来表示 */}
        {!isPc && !(hideTopEntryButton && !isStopped && !connectedFlag) && (
          <RecruitmentEntryButton
            connectedFlag={connectedFlag}
            recruitmentId={recruitmentId}
            companyId={state.companyId}
            isFirst={true}
            dataCategory={dataCategory}
            dataValue={dataValue}
            messageBoxId={messageBoxId}
            isShowMag={isShowMag}
            handleRegisterModalOpen={handleRegisterModalOpen}
            handleClickEntry={handleClickEntry}
            isPreview={isPreview}
            handlePreviewClick={handlePreviewClick}
            hideOnFirstView={isStoppedFv}
          />
        )}
      </div>
      {!isPc && (
        <>
          {isStoppedFv ? (
            <>
              <div ref={descriptionAnchorRef} />

              {isFixed && (
                <div
                  ref={descriptionRef}
                  className='description gtm-index-view-count fixed'
                  data-event-name='index on screen'
                  data-category={isStopped ? dataCategory.stopRecruitmentContent : dataCategory.recruitmentContent}
                  data-value={dataValue.index}
                  data-recruitmentid={recruitmentId}
                  data-companyid={state.companyId}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(8px)' }}
                >
                  {descriptionLinks}
                </div>
              )}
            </>
          ) : (
            <>
              {isFixed && <div style={{ height: descriptionHeight }} />}

              <div
                ref={descriptionRef}
                className={`description gtm-index-view-count ${isFixed ? 'fixed' : ''}`}
                data-event-name='index on screen'
                data-category={isStopped ? dataCategory.stopRecruitmentContent : dataCategory.recruitmentContent}
                data-value={dataValue.index}
                data-recruitmentid={recruitmentId}
                data-companyid={state.companyId}
                style={
                  isFixed
                    ? { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(8px)' }
                    : {}
                }
              >
                {descriptionLinks}
              </div>
            </>
          )}
        </>
      )
      }
    </>
  );
};
